// frontend/src/components/commandpalette/CommandPalette.jsx
// Global command palette — search pages, reports, actions.
//
// The app's popup shape (`styles/dialog-form.css`): the search box is the
// title bar, the results are the content, the keyboard hints are the button
// bar. It opens near the top of the window (`dialog-form--top`): its result
// list changes height with every keystroke, and a centred popup would move
// on every key.

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
} from '@mui/material';
import { ICON, ArrowDown, ArrowElbowDownLeft, ArrowUp, MagnifyingGlass, X } from '../../icons';
import './CommandPalette.css';

function CommandPalette({
  isOpen,
  close,
  query,
  setQuery,
  results,
  activeIndex,
  setActiveIndex,
  selectItem,
  handleKeyDown,
  inputRef,
}) {
  const { t } = useTranslation();
  const listRef = useRef(null);

  // Scroll active item into view
  useEffect(() => {
    if (!listRef.current) return;
    const active = listRef.current.querySelector('.cp-item--active');
    if (active) active.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const hasQuery = query.trim().length > 0;
  const hasResults = hasQuery
    ? results.grouped.length > 0
    : results.recent.length > 0;

  // Map flat index for active highlighting
  let flatIndex = 0;

  return (
    <Dialog
      open={isOpen}
      onClose={close}
      className="dialog-form dialog-form--confirm dialog-form--top command-palette"
      // MUI names a popup after its title bar. This one's title bar is the
      // search box, which would name the popup after what is typed in it, so
      // the popup is named outright and the title's id is not pointed at.
      slotProps={{ paper: { 'aria-label': t('commandPalette.title'), 'aria-labelledby': undefined } }}
    >
      {/* The title bar is the search box: a div, because the one heading a
          popup title gives would otherwise wrap an input. */}
      <DialogTitle component="div">
        <MagnifyingGlass size={ICON.xl} className="cp-input-icon" weight="bold" />
        <input
          ref={inputRef}
          type="text"
          className="cp-input"
          aria-label={t('commandPalette.title')}
          placeholder={t('commandPalette.placeholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          spellCheck={false}
        />
        <IconButton onClick={close} size="small" aria-label={t('common.close')}>
          <X size={ICON.lg} />
        </IconButton>
      </DialogTitle>

      <DialogContent className="cp-results" ref={listRef}>
        {!hasQuery && results.recent.length > 0 && (
          <div className="cp-group">
            <div className="cp-group-label">{t('commandPalette.recent')}</div>
            {results.recent.map((entry) => {
              const idx = flatIndex++;
              const Icon = entry.icon;
              return (
                <button
                  key={entry.id}
                  className={`cp-item ${idx === activeIndex ? 'cp-item--active' : ''}`}
                  onClick={() => selectItem(entry)}
                  onMouseEnter={() => setActiveIndex(idx)}
                >
                  <Icon size={ICON.lg} className="cp-item-icon" />
                  <div className="cp-item-content">
                    <span className="cp-item-label">{entry.label}</span>
                    {entry.description && (
                      <span className="cp-item-description">{entry.description}</span>
                    )}
                  </div>
                  <ArrowElbowDownLeft size={ICON.sm} className="cp-item-enter" />
                </button>
              );
            })}
          </div>
        )}

        {hasQuery &&
          results.grouped.map((group) => (
            <div className="cp-group" key={group.category}>
              <div className="cp-group-label">{group.label}</div>
              {group.items.map((entry) => {
                const idx = flatIndex++;
                const Icon = entry.icon;
                return (
                  <button
                    key={entry.id}
                    className={`cp-item ${idx === activeIndex ? 'cp-item--active' : ''}`}
                    onClick={() => selectItem(entry)}
                    onMouseEnter={() => setActiveIndex(idx)}
                  >
                    <Icon size={ICON.lg} className="cp-item-icon" />
                    <div className="cp-item-content">
                      <span className="cp-item-label">{entry.label}</span>
                      {entry.description && (
                        <span className="cp-item-description">{entry.description}</span>
                      )}
                    </div>
                    <ArrowElbowDownLeft size={ICON.sm} className="cp-item-enter" />
                  </button>
                );
              })}
            </div>
          ))}

        {!hasResults && hasQuery && (
          <div className="cp-empty">{t('commandPalette.noResults')}</div>
        )}

        {!hasResults && !hasQuery && (
          <div className="cp-empty">{t('commandPalette.startTyping')}</div>
        )}
      </DialogContent>

      {/* The button bar holds the keyboard hints: the keys are the palette's
          buttons. */}
      <DialogActions className="cp-footer">
        <span className="cp-footer-hint">
          <kbd className="cp-kbd"><ArrowUp size={ICON.xs} /></kbd>
          <kbd className="cp-kbd"><ArrowDown size={ICON.xs} /></kbd>
          <span>{t('commandPalette.navigate')}</span>
        </span>
        <span className="cp-footer-hint">
          <kbd className="cp-kbd"><ArrowElbowDownLeft size={ICON.xs} /></kbd>
          <span>{t('commandPalette.select')}</span>
        </span>
        <span className="cp-footer-hint">
          <kbd className="cp-kbd">ESC</kbd>
          <span>{t('commandPalette.close')}</span>
        </span>
      </DialogActions>
    </Dialog>
  );
}

export default CommandPalette;
